// Sitio público `/s/<slug>` o `<slug>.<dominio de sitios>` (fase 2 de «Sitios públicos»). Usa solo la API
// pública de `convex/sites/public.ts`, sin la sesión de la app: el token del
// miembro se guarda por sitio en localStorage (session.ts). Lo elegido en
// los selectores va en la URL (`picks`) y lo maneja la ruta.

import { Component, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { AlertTriangle, Globe } from "lucide-react";
import { api } from "@/convex/_generated/api";
import type { SitePageResult } from "@/convex/sites/public";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { SiteSkeletonBlock } from "@/convex/lib/schema/site_shape";
import { SiteBlocksSkeleton } from "./blocks/block-skeleton";
import { SiteBlocks, type SiteBlockActions } from "./blocks/site-blocks";
import { DeferredSiteBlock } from "./deferred-block";
import { clearSiteSession, readSiteSession, saveSiteSession } from "./session";
import { SiteLogin } from "./site-login";
import { SiteShell } from "./site-shell";
import { safeLogo, siteRootProps, type SiteTheme } from "./theme";
import "./site.css";

export type SitePicks = Record<string, string>;

export interface PublicSiteViewProps {
  slug: string;
  /** Página de la URL (sin ella, la primera). */
  page?: string;
  picks: SitePicks;
  /** Cambiar de página o de lo elegido (la ruta actualiza la URL). */
  onNavigate: (to: { page: string; picks: SitePicks }) => void;
  hrefFor: (page: string, picks: SitePicks) => string;
}

const EXPIRED_NOTICE = "Tu sesión terminó. Vuelve a entrar con tu correo.";

interface PublicSite {
  name: string;
  theme: SiteTheme;
  /** Textos de la pantalla de entrada (sin ellos, los de siempre). */
  login?: { title?: string; message?: string };
  /** `skeleton`: tipos y forma de los bloques, para el esqueleto de carga. */
  pages: Array<{ key: string; title: string; skeleton?: SiteSkeletonBlock[] }>;
}

export default function PublicSiteView(props: PublicSiteViewProps) {
  const { slug } = props;
  const site = useQuery(api.sites.public.getSite, { slug }) as PublicSite | null | undefined;
  const [session, setSession] = useState(() => readSiteSession(slug));
  const [notice, setNotice] = useState<string | undefined>();

  const requestCode = useAction(api.sites.public.requestCode);
  const verifyCode = useAction(api.sites.public.verifyCode);
  const renewSession = useMutation(api.sites.public.renewSession);
  const logout = useMutation(api.sites.public.logout);

  const signOut = useCallback(
    (expired: boolean) => {
      clearSiteSession(slug);
      setSession(null);
      setNotice(expired ? EXPIRED_NOTICE : undefined);
    },
    [slug],
  );

  // Renovar la sesión al abrir el sitio (el servidor la extiende con el uso).
  // Solo con el sitio encendido: apagado, `renewSession` también da null y
  // no hay que borrar una sesión que vuelve a valer al encenderlo.
  const token = session?.token;
  const live = !!site;
  useEffect(() => {
    if (!token || !live) return;
    let alive = true;
    renewSession({ slug, token })
      .then((renewed) => {
        if (!alive) return;
        if (renewed) saveSiteSession(slug, { token, expiresAt: renewed.expiresAt });
        else signOut(true);
      })
      .catch(() => {
        // Sin conexión: se intenta de nuevo en la próxima carga.
      });
    return () => {
      alive = false;
    };
  }, [live, renewSession, signOut, slug, token]);

  useEffect(() => {
    if (site?.name) document.title = site.name;
  }, [site?.name]);

  if (site === undefined) return <SiteLoading />;
  if (site === null) return <SiteUnavailable />;

  const root = siteRootProps(site.theme);
  const logo = safeLogo(site.theme.logo);

  return (
    <div {...root} lang="es" className={cn(root.className, "min-h-[100dvh]")}>
      {!token ? (
        <SiteLogin
          siteName={site.name}
          logo={logo}
          title={site.login?.title}
          message={site.login?.message}
          notice={notice}
          onRequestCode={(email) => requestCode({ slug, email })}
          onVerify={async (email, code) => {
            const opened = await verifyCode({ slug, email, code });
            saveSiteSession(slug, opened);
            setNotice(undefined);
            setSession(opened);
          }}
        />
      ) : (
        <SiteErrorBoundary
          resetKey={`${props.page ?? ""}|${JSON.stringify(props.picks)}`}
          fallback={(reset) => (
            <SiteShell
              siteName={site.name}
              logo={logo}
              pages={site.pages}
              current={props.page ?? site.pages[0]?.key}
              hrefFor={(p) => props.hrefFor(p, props.picks)}
              onNavigate={(p) => props.onNavigate({ page: p, picks: props.picks })}
              onLogout={() => {
                void logout({ token }).catch(() => undefined);
                signOut(false);
              }}
            >
              <SiteError
                onRetry={() => {
                  // Lo elegido pudo dejar de estar disponible: se empieza de nuevo.
                  reset();
                  props.onNavigate({ page: props.page ?? site.pages[0]?.key ?? "", picks: {} });
                }}
              />
            </SiteShell>
          )}
        >
          <MemberArea
            {...props}
            site={site}
            logo={logo}
            token={token}
            onExpired={() => signOut(true)}
            onLogout={() => {
              void logout({ token }).catch(() => undefined);
              signOut(false);
            }}
          />
        </SiteErrorBoundary>
      )}
    </div>
  );
}

type OkPage = Extract<SitePageResult, { status: "ok" }>;

function MemberArea({
  slug,
  page,
  picks,
  onNavigate,
  hrefFor,
  site,
  logo,
  token,
  onExpired,
  onLogout,
}: PublicSiteViewProps & {
  site: PublicSite;
  logo?: string;
  token: string;
  onExpired: () => void;
  onLogout: () => void;
}) {
  const pageKey = page ?? site.pages[0]?.key;
  const known = !!pageKey && site.pages.some((p) => p.key === pageKey);
  const result = useQuery(
    api.sites.public.getPage,
    known ? { slug, token, page: pageKey!, ...(Object.keys(picks).length ? { picks } : {}) } : "skip",
  ) as SitePageResult | undefined;
  const submit = useMutation(api.sites.public.submit);
  const downloadPdf = useAction(api.sites.pdf.downloadPdf);

  // Mientras llega la página con otra elección, se deja la anterior (sin
  // parpadeo); al cambiar de página, el esqueleto de sus bloques.
  const last = useRef<OkPage | null>(null);
  if (result?.status === "ok") last.current = result;
  const shown = result?.status === "ok" ? result : last.current?.page.key === pageKey ? last.current : null;
  const refreshing = result === undefined && !!shown;

  useEffect(() => {
    if (result?.status === "signed_out") onExpired();
  }, [result?.status, onExpired]);

  useEffect(() => {
    if (shown) document.title = `${shown.page.title} · ${site.name}`;
  }, [shown, site.name]);

  if (result?.status === "not_found") return <SiteUnavailable />;

  const withPicks = Object.keys(picks).length ? { picks } : {};
  const actions: SiteBlockActions = {
    onPick: (source, id) => onNavigate({ page: pageKey!, picks: { ...picks, [source]: id } }),
    onSubmit: (block, values) => submit({ slug, token, page: pageKey!, block, values, ...withPicks }),
    onDownload: (block, choice) =>
      downloadPdf({ slug, token, page: pageKey!, block, ...withPicks, ...(choice ? { choice } : {}) }),
  };

  const nav = {
    siteName: site.name,
    logo,
    memberLabel: shown?.member.label ?? last.current?.member.label,
    pages: site.pages,
    current: pageKey,
    hrefFor: (p: string) => hrefFor(p, picks),
    onNavigate: (p: string) => onNavigate({ page: p, picks }),
    onLogout,
  };

  if (!known) {
    return (
      <SiteShell {...nav}>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-lg font-medium text-foreground">No encontramos esta página</p>
          <p className="text-sm text-muted-foreground">Puede que el enlace haya cambiado.</p>
          {site.pages[0] && (
            <Button onClick={() => onNavigate({ page: site.pages[0].key, picks })}>Ir a {site.pages[0].title}</Button>
          )}
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell {...nav}>
      {shown ? (
        <div className={cn("transition-opacity", refreshing && "pointer-events-none opacity-60")} aria-busy={refreshing}>
          <SiteBlocks
            blocks={shown.blocks}
            {...actions}
            renderDeferred={(placeholder) => (
              <DeferredSiteBlock
                slug={slug}
                token={token}
                page={shown.page.key}
                placeholder={placeholder}
                picks={picks}
                onExpired={onExpired}
                actions={actions}
              />
            )}
          />
        </div>
      ) : (
        <PageSkeleton layout={site.pages.find((p) => p.key === pageKey)?.skeleton} />
      )}
    </SiteShell>
  );
}

// ── Estados ─────────────────────────────────────────────────────────────────

/** Los bloques de la página como esqueletos (el genérico si el servidor no manda la forma). */
function PageSkeleton({ layout }: { layout?: SiteSkeletonBlock[] }) {
  return layout?.length ? <SiteBlocksSkeleton blocks={layout} /> : <BlocksSkeleton />;
}

/** Esqueleto genérico: antes de saber qué página es (`getSite` todavía no responde). */
function BlocksSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Cargando">
      <Skeleton className="h-7 w-2/3 max-w-sm" />
      <div className="flex gap-2">
        <Skeleton className="h-9 w-24 rounded-full" />
        <Skeleton className="h-9 w-24 rounded-full" />
      </div>
      <div className="grid grid-cols-2 gap-3 @min-[40rem]:grid-cols-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <Skeleton className="h-48" />
    </div>
  );
}

function SiteLoading() {
  return (
    <div className="public-site min-h-[100dvh]" data-preset="heskala">
      <div className="border-b bg-card">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          <Skeleton className="size-8" />
          <Skeleton className="h-5 w-40" />
        </div>
      </div>
      <div className="mx-auto max-w-5xl px-4 py-8">
        <BlocksSkeleton />
      </div>
    </div>
  );
}

function SiteUnavailable() {
  return (
    <div className="public-site flex min-h-[100dvh] items-center justify-center px-6" data-preset="heskala">
      <div className="max-w-sm text-center">
        <Globe className="mx-auto size-10 text-muted-foreground" aria-hidden />
        <h1 className="mt-4 text-xl font-semibold text-foreground">Este sitio no está disponible</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Revisa que el enlace esté bien escrito. Si te lo compartieron, pide uno nuevo a quien te lo envió.
        </p>
      </div>
    </div>
  );
}

function SiteError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center" role="alert">
      <AlertTriangle className="size-8 text-destructive" aria-hidden />
      <p className="text-lg font-medium text-foreground">No pudimos cargar esta página</p>
      <p className="max-w-sm text-sm text-muted-foreground">Puede ser un problema de conexión o algo que ya no está disponible.</p>
      <Button onClick={onRetry}>Volver a intentar</Button>
    </div>
  );
}

/** Errores al leer una página (p. ej. algo elegido que ya no es del miembro). */
class SiteErrorBoundary extends Component<
  { resetKey: string; fallback: (reset: () => void) => ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidUpdate(prev: { resetKey: string }) {
    // Otra página u otra elección: se vuelve a intentar.
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }
  reset = () => this.setState({ failed: false });
  render() {
    return this.state.failed ? this.props.fallback(this.reset) : this.props.children;
  }
}
